import { db } from './firebase/config';
import { collection, addDoc, getDoc, getDocs, doc, query, where, serverTimestamp, updateDoc, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getAppSettings } from '../firebase/services/appSettingsService';
import axios from 'axios';

/**
 * Service pour la gestion des résumés de transcription
 */
const summaryService = {
  /**
   * Récupère un résumé par ID
   * @param {string} id - ID du résumé
   * @returns {Promise<object>} Résumé
   */
  getSummaryById: async (id) => {
    try {
      const summaryRef = doc(db, 'summaries', id);
      const summarySnap = await getDoc(summaryRef);
      
      if (summarySnap.exists()) {
        const summaryData = { id: summarySnap.id, ...summarySnap.data() };
        return summaryData;
      }
      return null;
    } catch (error) {

      throw error;
    }
  },
  
  /**
   * Récupère un résumé par ID de transcription
   * @param {string} transcriptionId - ID de la transcription
   * @param {object} options - Options de récupération (incluant isAdmin)
   * @returns {Promise<object>} Résumé
   */
  getSummaryByTranscriptionId: async (transcriptionId, options = {}) => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Utilisateur non authentifié');
      }
      
      const summariesRef = collection(db, 'summaries');
      
      // Si l'utilisateur est admin, ne pas filtrer par userId
      let q;
      if (options.isAdmin) {
        q = query(summariesRef, 
          where('transcriptionId', '==', transcriptionId)
        );
      } else {
        q = query(summariesRef, 
          where('transcriptionId', '==', transcriptionId),
          where('userId', '==', user.uid)
        );
      }
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const summaryData = { id: doc.id, ...doc.data() };
        return summaryData;
      }
      
      return null;
    } catch (error) {

      throw error;
    }
  },
  
  /**
   * Génère un résumé pour une transcription
   * @param {string} transcriptionId - ID de la transcription
   * @param {object} options - Options de génération
   * @returns {Promise<object>} Résumé généré
   */
  generateSummary: async (transcriptionId, options = {}) => {
    // Déclarer summaryId au niveau de la fonction pour qu'il soit accessible dans le catch
    let summaryId = options.summaryId || null;

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('Utilisateur non authentifié');
      }

      // ===== Récupérer le token Azure pour l'envoi automatique d'email =====
      // Utilise la méthode stricte qui ne retourne JAMAIS un token expiré
      let azureToken = null;
      try {
        // Importer dynamiquement authService pour récupérer le token Azure
        const { default: authService } = await import('./authService');

        console.log('🔍 [SummaryService] Récupération du token Azure pour l\'envoi d\'email...');

        // Utiliser la méthode STRICTE qui garantit un token valide ou throw une erreur
        // Cette méthode ne retourne jamais un token expiré contrairement aux anciennes méthodes
        azureToken = await authService.getValidAzureTokenStrict();

        console.log('✅ [SummaryService] Token Azure valide obtenu pour l\'envoi d\'email');

      } catch (azureError) {
        // L'erreur est attendue si le token est expiré et ne peut pas être rafraîchi
        console.warn('⚠️ [SummaryService] Token Azure non disponible:', azureError.message);
        console.log('ℹ️ [SummaryService] La génération du résumé continuera sans envoi d\'email automatique');
        // azureToken reste null, la génération du résumé fonctionnera quand même
      }

      // Récupérer les paramètres de l'application
      const appSettings = await getAppSettings();
      const openAISettings = appSettings.openAISettings || {};
      const summarySettings = appSettings.summarySettings || {};
      
      // Si un ID de résumé est fourni, utiliser celui-là
      let summaryId = options.summaryId;
      let summaryRef;
      
      if (!summaryId) {
        // Créer un nouveau document résumé "En cours" dans Firestore
        summaryRef = await addDoc(collection(db, 'summaries'), {
          transcriptionId,
          userId: user.uid,
          status: 'En cours',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          model: options.model || summarySettings.defaultModel || 'gpt-3.5-turbo',
          promptModelId: options.promptModelId || null,
        });
        summaryId = summaryRef.id;
      } else {
        // Utiliser le résumé existant
        summaryRef = doc(db, 'summaries', summaryId);
        await updateDoc(summaryRef, {
          updatedAt: serverTimestamp(),
          promptModelId: options.promptModelId || null,
        });
      }
      
      // Préparer les options complètes avec les paramètres de l'application
      const completeOptions = {
        model: options.model || summarySettings.defaultModel || 'gpt-3.5-turbo',
        systemPrompt: options.prompt || openAISettings.summaryPrompt ||
          "Vous êtes un assistant qui résume des transcriptions de réunions.\n\n" +
          "INSTRUCTIONS :\n" +
          "- Créez un résumé STRUCTURÉ EN MARKDOWN avec des titres (#, ##), du gras (**texte**), des listes (-), etc.\n" +
          "- Le résumé doit être clair, concis et bien formaté\n" +
          "- Mettez en évidence les points clés, les décisions prises et les actions à entreprendre\n" +
          "- Utilisez des tableaux Markdown si pertinent pour la clarté\n\n" +
          "Le résumé doit être visuellement attractif et facile à parcourir.",
        maxTokens: summarySettings.maxTokens || 16384,
        temperature: openAISettings.temperature || 0.7,
        userMessage: options.userMessage || '',
        summaryId: summaryId,
        promptModelId: options.promptModelId || null,
        selectedPreviousMeetingIds: options.selectedPreviousMeetingIds || null,
      };
      
      const idToken = await user.getIdToken(true);
      const url = 'https://us-central1-diotranscript.cloudfunctions.net/generateSummary';
      const response = await axios.post(url,
        {
          data: {
            transcriptionId,
            options: completeOptions,
            azureToken: azureToken // ===== NOUVEAU : Inclure le token Azure =====
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${idToken}`
          },
          timeout: 600000 // 10 minutes de timeout côté client (> 540s du serveur)
        }
      );
      
      // Récupérer le résultat de la fonction Cloud
      let result = response.data.result;
      
      // ===== NOUVEAU : Log du statut d'envoi de l'email =====
      if (result.emailSent) {
      } else if (result.emailError) {

      }
      
      // S'assurer que l'ID est présent dans le résultat
      if (!result.id && summaryId) {
        result.id = summaryId;
      }
      
      return result;
    } catch (error) {
      // Amélioration du message d'erreur pour plus de clarté
      let errorMessage = 'Erreur lors de la génération du résumé';
      let isTimeout = false;

      // Détection spécifique du timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMessage = 'La génération du résumé a pris trop de temps. La transcription est peut-être trop longue. Veuillez réessayer ou contacter le support si le problème persiste.';
        isTimeout = true;
        console.error('[SummaryService] Timeout détecté:', error.message);
      } else if (error.response) {
        // Erreur HTTP de la fonction Cloud
        const status = error.response.status;
        const serverMessage = error.response.data?.message;

        if (status === 403) {
          errorMessage = 'Vous n\'avez pas l\'autorisation de générer ce résumé. Veuillez vous reconnecter.';
        } else if (status === 404) {
          errorMessage = 'Transcription non trouvée. Veuillez réessayer.';
        } else if (status === 400) {
          errorMessage = serverMessage || 'Paramètres de génération invalides.';
        } else if (status === 500) {
          errorMessage = `Erreur serveur: ${serverMessage || 'Problème lors de l\'appel à l\'API OpenAI'}`;
        } else if (status === 504) {
          // Gateway Timeout - le serveur Firebase a timeout
          errorMessage = 'Le serveur a mis trop de temps à répondre. La transcription est peut-être très longue. Veuillez réessayer.';
          isTimeout = true;
        } else if (serverMessage) {
          errorMessage = serverMessage;
        } else {
          errorMessage = `Erreur ${status}: ${error.response.statusText}`;
        }

        console.error('[SummaryService] Erreur HTTP:', {
          status,
          message: serverMessage,
          url: error.response.config?.url
        });
      } else if (error.message) {
        // Erreur réseau ou autre
        if (error.message.includes('Network Error')) {
          errorMessage = 'Erreur de connexion. Vérifiez votre connexion internet et réessayez.';
        } else {
          errorMessage = error.message;
        }
        console.error('[SummaryService] Erreur:', error.message);
      }

      // En cas de timeout ou d'erreur, marquer le résumé en erreur dans Firestore
      // pour que l'utilisateur soit alerté via le listener temps réel
      if (summaryId) {
        try {
          const summaryRef = doc(db, 'summaries', summaryId);
          await updateDoc(summaryRef, {
            status: 'Erreur',
            error: errorMessage,
            errorDetails: {
              isTimeout: isTimeout,
              timestamp: new Date().toISOString(),
              originalError: error.message || 'Unknown error'
            },
            updatedAt: serverTimestamp()
          });
          console.log('[SummaryService] Résumé marqué en erreur dans Firestore:', summaryId);
        } catch (updateError) {
          console.error('[SummaryService] Impossible de mettre à jour le statut du résumé:', updateError);
        }
      }

      // Re-throw avec le message amélioré
      const enhancedError = new Error(errorMessage);
      enhancedError.originalError = error;
      enhancedError.response = error.response;
      enhancedError.isTimeout = isTimeout;
      throw enhancedError;
    }
  },
  
  /**
   * Met à jour un résumé existant
   * @param {string} summaryId - ID du résumé
   * @param {object} data - Données à mettre à jour (ex: { text })
   * @returns {Promise<void>}
   */
  updateSummary: async (summaryId, data) => {
    try {
      const summaryRef = doc(db, 'summaries', summaryId);
      await updateDoc(summaryRef, {
        ...data,
        updatedAt: serverTimestamp()
      });
    } catch (error) {

      throw error;
    }
  },

  /**
   * Ecoute en temps réel un résumé par ID
   * @param {string} id
   * @param {function} callback
   * @returns {function}
   */
  onSummaryById: (id, callback) => {
    const summaryRef = doc(db, 'summaries', id);
    return onSnapshot(summaryRef, (docSnap) => {
      if (docSnap.exists()) {
        const summaryData = { id: docSnap.id, ...docSnap.data() };
        callback(summaryData);
      } else {
        callback(null);
      }
    }, (error) => {

      callback(null);
    });
  },

  /**
   * Ecoute en temps réel un résumé par transcriptionId
   * @param {string} transcriptionId
   * @param {function} callback
   * @param {object} options - Options incluant isAdmin
   * @returns {function}
   */
  onSummaryByTranscriptionId: (transcriptionId, callback, options = {}) => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) throw new Error('Utilisateur non authentifié');
    const summariesRef = collection(db, 'summaries');
    
    // Si l'utilisateur est admin, ne pas filtrer par userId
    let q;
    if (options.isAdmin) {
      q = query(summariesRef, 
        where('transcriptionId', '==', transcriptionId)
      );
    } else {
      q = query(summariesRef, 
        where('transcriptionId', '==', transcriptionId),
        where('userId', '==', user.uid)
      );
    }
    
    return onSnapshot(q, (querySnapshot) => {
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const summaryData = { id: doc.id, ...doc.data() };
        callback(summaryData);
      } else {
        callback(null);
      }
    }, (error) => {

      // En cas d'erreur de permissions, renvoyer null
      callback(null);
    });
  },

  /**
   * Ecoute en temps réel tous les résumés d'un utilisateur
   * @param {string} userId
   * @param {function} callback
   * @returns {function}
   */
  onUserSummaries: (userId, callback) => {
    const summariesRef = collection(db, 'summaries');
    const q = query(summariesRef, where('userId', '==', userId));
    return onSnapshot(q, (querySnapshot) => {
      const summaries = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      callback(summaries);
    }, (error) => {

      callback([]);
    });
  },

  /**
   * Annule un résumé en cours de génération
   * @param {string} summaryId - ID du résumé
   * @returns {Promise<void>}
   */
  cancelSummary: async (summaryId) => {
    try {
      const summaryRef = doc(db, 'summaries', summaryId);
      await updateDoc(summaryRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });
    } catch (error) {

      throw error;
    }
  }
};

export default summaryService; 